/**
 * iManage settings: the person's own connection (everyone), and the
 * organization's iManage application and scope (admins). Moved here from the
 * old single iManage page; the forms and their rules are unchanged.
 *
 * @module components/apps/imanage/IManageSettingsPage
 */
import { useState, type ReactNode } from "react";

import { useIntegrations } from "../../../state/governance";
import { IntegrationScopeForm } from "../integrations/IntegrationScopeForm";
import { IntegrationSetupForm } from "../integrations/IntegrationSetupForm";
import { INTEGRATION_SCOPE_SPECS } from "../integrations/integrationContent";
import { Button } from "../../ui/button";
import { useIManageArea } from "./imanageAreaContext";
import { IManageConnectionCard } from "./IManageLanding";
import { IManagePageHeader } from "./IManageLayout";
import { InlineError } from "./IManageParts";
import { invalidateIManage } from "./imanageQueries";

function Section({
  title,
  description,
  children,
}: {
  readonly title: string;
  readonly description?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <section className="grid gap-x-10 gap-y-4 border-b border-border/60 py-8 last:border-0 md:grid-cols-[14rem_1fr]">
      <div>
        <h2 className="text-sm font-semibold">{title}</h2>
        {description ? <p className="mt-1 text-[13px] text-pretty text-muted-foreground">{description}</p> : null}
      </div>
      <div className="min-w-0 space-y-4">{children}</div>
    </section>
  );
}

export function IManageSettingsPage() {
  const { organizationId, status, canManage, refreshStatus } = useIManageArea();
  const { snapshot, setCredentials, setScope, setEnabled, clearCredentials } =
    useIntegrations(organizationId);
  const integration = snapshot?.integrations.find((x) => x.id === "imanage");
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<string | null | void>) {
    setBusy(true);
    setError(null);
    try {
      const message = await action();
      if (typeof message === "string") throw new Error(message);
      if (organizationId) invalidateIManage(organizationId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "The request failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <IManagePageHeader title={<h1 className="text-[15px] font-semibold">Settings</h1>} />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-4xl px-6">
          {error ? <InlineError className="mt-6" message={error} /> : null}
          <Section
            title="Your connection"
            description="Amoreg reads iManage with your own account and permissions. Other members connect their own."
          >
            <IManageConnectionCard
              organizationId={organizationId}
              status={status}
              onChanged={refreshStatus}
            />
            <p className="text-xs text-muted-foreground">
              Allowed for your organization: {status.operations.length ? status.operations.join(", ") : "nothing yet"}.
              {status.canUpload ? " Saving back to iManage always asks you to confirm the exact file first." : ""}
            </p>
          </Section>
          {canManage ? (
            <>
              <Section
                title="iManage application"
                description="Register Amoreg as an OAuth application with iManage and enable it in your firm's Cloud Control Center. These settings do not grant access to documents."
              >
                {status.callbackUri ? (
                  <div className="text-[13px]">
                    <p className="text-muted-foreground">OAuth redirect URI to register</p>
                    <code className="mt-1 block rounded-md bg-muted px-2 py-1.5 font-mono text-xs break-all">
                      {status.callbackUri}
                    </code>
                  </div>
                ) : null}
                <IntegrationSetupForm
                  connectorId="imanage"
                  values={values}
                  onChange={setValues}
                  disabled={busy}
                  submitLabel="Save application settings"
                  onSubmit={(chosen) =>
                    void run(async () => {
                      const message = await setCredentials("imanage", chosen);
                      if (!message) setValues({});
                      return message;
                    })
                  }
                />
              </Section>
              <Section
                title="Scope"
                description="Which libraries and operations Amoreg may use. Each person is still limited by their own iManage permissions."
              >
                <IntegrationScopeForm
                  key={JSON.stringify(integration?.scope)}
                  organizationId={organizationId}
                  connectorId="imanage"
                  configured={integration?.configured === true}
                  spec={INTEGRATION_SCOPE_SPECS.imanage!}
                  scope={integration?.scope ?? null}
                  disabled={busy}
                  canManage={canManage}
                  onSave={(scope) =>
                    void run(() => setScope("imanage", scope))
                  }
                />
              </Section>
              <Section title="Availability" description="Turn iManage on or off for everyone in the organization.">
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || !integration?.configured}
                    onClick={() => void run(() => setEnabled("imanage", !integration?.enabled))}
                  >
                    {integration?.enabled ? "Disable for organization" : "Enable for organization"}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive-outline"
                    disabled={busy || !integration?.configured}
                    onClick={() => {
                      if (
                        window.confirm(
                          "Remove the organization's iManage application settings? Members will lose access through Amoreg.",
                        )
                      )
                        void run(() => clearCredentials("imanage"));
                    }}
                  >
                    Remove application settings
                  </Button>
                </div>
              </Section>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
